import { useParams, useNavigate } from 'react-router-dom';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useCategoryDetail } from '@/hooks/useCategoryDetail';
import { ChevronLeft } from 'lucide-react';
import Colors from '@/constants/colors';
import PageContainer from '@/components/PageContainer';
import ContentSection from '@/components/ContentSection';
import CompactCard from '@/components/CompactCard';
import PromotedCarousel from '@/components/PromotedCarousel';
import { extractPlainText } from '@/lib/lexical-parser';

export default function CategoryDetail() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const navigate = useNavigate();
  const { language } = useAppLanguage();

  const { category, places, articles, isLoading, error } = useCategoryDetail(categorySlug || '', language);

  if (!categorySlug) {
    return (
      <PageContainer>
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p>Category not found</p>
        </ContentSection>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p>Loading...</p>
        </ContentSection>
      </PageContainer>
    );
  }

  if (error || !category) {
    return (
      <PageContainer>
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p style={{ color: Colors.error }}>Error loading category</p>
        </ContentSection>
      </PageContainer>
    );
  }

  const promotedPlaces = places.filter((p) => p.promoted);
  const regularPlaces = places.filter((p) => !p.promoted);
  const featuredArticles = articles.filter((a) => a.featured);
  const regularArticles = articles.filter((a) => !a.featured);

  const getName = (place: { name: string; name_ru?: string }) =>
    language === 'ru' ? place.name_ru || place.name : place.name;

  const getDescription = (place: { description?: string; description_ru?: string; summary?: string }) =>
    language === 'ru' ? place.description_ru || place.description : place.description || place.summary;

  const getTitle = (article: { title: string; title_ru?: string }) =>
    language === 'ru' ? article.title_ru || article.title : article.title;

  const getSubtitle = (article: { subtitle?: string; subtitle_ru?: string }) =>
    language === 'ru' ? article.subtitle_ru || article.subtitle : article.subtitle;

  return (
    <PageContainer>
      {/* Hero Section */}
      <div style={styles.heroContainer}>
        <img
          src={category.imageUrl}
          alt={category.name}
          style={styles.heroImage}
          loading="lazy"
          decoding="async"
        />
        <div style={styles.heroOverlay} />
        <div style={styles.heroContent}>
          <button
            style={styles.backButton}
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ChevronLeft size={24} color="white" />
          </button>
          <h1 style={styles.heroTitle}>{category.name}</h1>
          <p style={styles.heroDescription}>{category.description}</p>
        </div>
      </div>

      {/* Featured Articles */}
      {featuredArticles.length > 0 && (
        <div style={{ padding: '24px' }}>
          <h2 style={styles.sectionTitle}>Featured Articles</h2>
          <ContentSection style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0' }}>
            {featuredArticles.map((article) => (
              <CompactCard
                key={article.id}
                id={article.id}
                imageUrl={article.imageUrl}
                imageAlt={getTitle(article)}
                title={getTitle(article)}
                description={getSubtitle(article) || ''}
                promoted={article.featured}
                onCardClick={() => navigate(`/articles/${article.id}`, { state: { fromApp: true } })}
              />
            ))}
          </ContentSection>
        </div>
      )}

      {/* All Articles */}
      {regularArticles.length > 0 && (
        <div style={{ padding: '0 24px 24px' }}>
          <h2 style={styles.sectionTitle}>Articles</h2>
          <ContentSection style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0' }}>
            {regularArticles.map((article) => (
              <CompactCard
                key={article.id}
                id={article.id}
                imageUrl={article.imageUrl}
                imageAlt={getTitle(article)}
                title={getTitle(article)}
                description={getSubtitle(article) || ''}
                onCardClick={() => navigate(`/articles/${article.id}`, { state: { fromApp: true } })}
              />
            ))}
          </ContentSection>
        </div>
      )}

      {/* Promoted Places Carousel */}
      {promotedPlaces.length > 0 && (
        <div style={{ padding: '24px' }}>
          <h2 style={styles.sectionTitle}>Featured Places</h2>
          <ContentSection style={{ padding: '0' }}>
            <PromotedCarousel
              places={promotedPlaces}
              language={language}
              onPlaceClick={(placeId) => navigate(`/places/${placeId}`, { state: { fromApp: true } })}
            />
          </ContentSection>
        </div>
      )}

      {/* All Places */}
      {regularPlaces.length > 0 && (
        <div style={{ padding: '0 24px 24px' }}>
          <h2 style={styles.sectionTitle}>Places</h2>
          <ContentSection style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0' }}>
            {regularPlaces.map((place) => (
              <CompactCard
                key={place.id}
                id={place.id}
                imageUrl={place.image_url || place.imageUrl}
                imageAlt={getName(place)}
                title={getName(place)}
                description={extractPlainText(getDescription(place) || '').substring(0, 80) + '...' || 'No description'}
                location={place.location}
                promoted={place.promoted}
                onCardClick={() => navigate(`/places/${place.id}`, { state: { fromApp: true } })}
              />
            ))}
          </ContentSection>
        </div>
      )}

      {/* Empty state if no content */}
      {articles.length === 0 && places.length === 0 && (
        <ContentSection style={{ padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: Colors.textSecondary }}>No content found in this category</p>
        </ContentSection>
      )}

      {/* Spacing for bottom nav */}
      <div style={{ height: '40px' }} />
    </PageContainer>
  );
}

const styles = {
  heroContainer: {
    position: 'relative',
    width: '100%',
    height: '300px',
    overflow: 'hidden',
  } as React.CSSProperties,

  heroImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  } as React.CSSProperties,

  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
  } as React.CSSProperties,

  heroContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '16px 24px',
  } as React.CSSProperties,

  backButton: {
    alignSelf: 'flex-start',
    background: 'rgba(0,0,0,0.4)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
  } as React.CSSProperties,

  heroTitle: {
    margin: 0,
    fontSize: '32px',
    fontWeight: '700',
    color: 'white',
    lineHeight: '1.2',
  } as React.CSSProperties,

  heroDescription: {
    margin: '12px 0 0 0',
    fontSize: '14px',
    color: 'rgba(255,255,255,0.9)',
    lineHeight: '1.5',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '20px',
    fontWeight: '700',
    margin: '0 0 16px 0',
    color: Colors.text,
  } as React.CSSProperties,
};
